import React, { useState, useRef } from 'react';
import { 
  Share2, 
  Printer, 
  Sparkles, 
  Palette, 
  Layers, 
  Download, 
  CheckCircle2, 
  DollarSign, 
  Zap, 
  Users, 
  ClipboardCheck 
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Props {
  tenantId: string;
}

const styleConfigs = {
  indigo: {
    primaryBg: 'bg-indigo-600',
    primaryText: 'text-indigo-600',
    lightBg: 'bg-indigo-50/70',
    borderColor: 'border-indigo-150',
    darkText: 'text-indigo-950',
    gradient: 'from-indigo-650 to-blue-600',
  },
  emerald: {
    primaryBg: 'bg-emerald-600',
    primaryText: 'text-emerald-600',
    lightBg: 'bg-emerald-50/70',
    borderColor: 'border-emerald-150',
    darkText: 'text-emerald-950',
    gradient: 'from-emerald-700 to-teal-600',
  },
  charcoal: {
    primaryBg: 'bg-slate-900',
    primaryText: 'text-slate-800',
    lightBg: 'bg-slate-100/70',
    borderColor: 'border-slate-200',
    darkText: 'text-slate-950',
    gradient: 'from-slate-900 to-slate-800',
  },
  amber: {
    primaryBg: 'bg-amber-600',
    primaryText: 'text-amber-600',
    lightBg: 'bg-amber-50/70',
    borderColor: 'border-amber-150',
    darkText: 'text-amber-950',
    gradient: 'from-amber-600 to-orange-500',
  },
};

export const ReferralProgram: React.FC<Props> = ({ tenantId }) => {
  const [referralCode] = useState(() => `TF-OWNER-${tenantId.substring(0, 5).toUpperCase()}`);
  const [referralLinkCopied, setReferralLinkCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'link' | 'flyer'>('link');

  // Flyer customizer states
  const [flyerStyle, setFlyerStyle] = useState<'indigo' | 'emerald' | 'charcoal' | 'amber'>('indigo');
  const [customCta, setCustomCta] = useState('Claim Your 30-Day Free Trial + Full System Walkthrough');
  const [customNote, setCustomNote] = useState('Personalized 1-on-1 setup with hospitality operations experts.');
  const flyerRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    const element = flyerRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF('p', 'mm', 'letter');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save('flyer.pdf');
  };

  const getSharingLink = () => {
    const origin = window.location.origin;
    return `${origin}/signup?referral=${referralCode}`;
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(getSharingLink());
    setReferralLinkCopied(true);
    setTimeout(() => setReferralLinkCopied(false), 2500);
  };

  return (
    <div className="space-y-6">
      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 no-print">
        <button
          onClick={() => setActiveTab('link')}
          className={`py-3 px-6 text-xs font-black uppercase tracking-widest border-b-2 transition-all cursor-pointer ${
            activeTab === 'link'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-400 hover:text-gray-700'
          }`}
        >
          Referral Link & Info
        </button>
        <button
          onClick={() => setActiveTab('flyer')}
          className={`py-3 px-6 text-xs font-black uppercase tracking-widest border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'flyer'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-400 hover:text-gray-700'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-indigo-500 animate-pulse" /> Promo Flyer Creator
        </button>
      </div>

      {activeTab === 'link' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Your Custom Referral Program Link:</span>
            
            <div className="flex gap-2">
              <div className="bg-gray-50 rounded-xl px-4 py-3 font-mono text-xs text-gray-800 font-bold border border-gray-200 select-all grow flex items-center justify-between">
                <span>{getSharingLink()}</span>
              </div>
              <button
                onClick={handleCopyReferral}
                className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-xs tracking-wider rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
              >
                {referralLinkCopied ? 'Copied' : <><Share2 className="w-4 h-4" /> Copy Link</>}
              </button>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                <span><strong>Referral Code</strong>: {referralCode}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span><strong>Reward</strong>: 10% Monthly Lifetime Subscription Credit</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                <span><strong>Payback Method</strong>: Automatic Invoice Credit</span>
              </div>
            </div>
          </div>

          {/* Referral Reward Breakdown Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-black text-sm">
                10%
              </div>
              <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">10% Lifetime Billing Credit</h4>
              <p className="text-[11px] text-gray-500 font-medium leading-relaxed">
                Receive an automatic 10% recurring credit applied to your monthly TipFlow subscription for as long as your referred venue remains active.
              </p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-sm">
                30d
              </div>
              <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">30-Day Free Trial for Peers</h4>
              <p className="text-[11px] text-gray-500 font-medium leading-relaxed">
                Your invited peers receive a full 30-day free trial with complete tip pool setup, POS integration support, and zero upfront commitment.
              </p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
              <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-black text-sm">
                ∞
              </div>
              <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Stackable to $0/Month</h4>
              <p className="text-[11px] text-gray-500 font-medium leading-relaxed">
                There are zero limits on referral credits. Refer 10 venues and completely offset 100% of your TipFlow software subscription costs.
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* PROMO FLYER CREATOR WORKSPACE */
        <div className="space-y-6">

          {/* Desktop Layout Split */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            
            {/* LEFT COLUMN: Customizer Controls (Hidden when printing) */}
            <div className="lg:col-span-4 bg-white rounded-2xl border border-gray-150 p-5 shadow-sm space-y-5 no-print">
              <div className="space-y-1">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Palette className="w-4 h-4 text-indigo-600" /> Flyer Customizer
                </h3>
                <p className="text-[10px] text-gray-400">Tailor the visual theme and invitation copy below.</p>
              </div>

              {/* Theme selection */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Visual Theme Accent</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'indigo', name: 'Indigo', color: 'bg-indigo-600 border-indigo-200' },
                    { id: 'emerald', name: 'Emerald', color: 'bg-emerald-600 border-emerald-200' },
                    { id: 'charcoal', name: 'Charcoal', color: 'bg-slate-900 border-slate-300' },
                    { id: 'amber', name: 'Warm Amber', color: 'bg-amber-600 border-amber-200' }
                  ].map(theme => (
                    <button
                      key={theme.id}
                      onClick={() => setFlyerStyle(theme.id as any)}
                      className={`flex flex-col items-center gap-1 p-2 border rounded-xl hover:bg-slate-50 transition-all cursor-pointer ${
                        flyerStyle === theme.id ? 'border-slate-800 ring-2 ring-slate-100' : 'border-gray-200'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full ${theme.color} shadow-inner block`} />
                      <span className="text-[9px] font-bold text-gray-500">{theme.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA Customizer */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Special Promotional Call-To-Action</label>
                <textarea
                  rows={2}
                  value={customCta}
                  onChange={(e) => setCustomCta(e.target.value)}
                  placeholder="e.g., Claim Your 30-Day Free Trial + Full Onboarding Support"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none resize-none"
                />
              </div>

              {/* Footnote details */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Personalized Footer Note</label>
                <textarea
                  rows={2}
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  placeholder="e.g., Personalized 1-on-1 setup with hospitality operations experts."
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none resize-none"
                />
              </div>

              {/* Campaign Code Display */}
              <div className="p-3 bg-slate-50 border border-gray-150 rounded-xl space-y-1">
                <span className="text-[9px] font-black uppercase text-gray-400 tracking-wider">Assigned Campaign Code</span>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-black text-slate-800 uppercase bg-white border border-gray-200 px-2.5 py-1 rounded">
                    {referralCode}
                  </span>
                  <span className="text-[9px] text-gray-400 font-semibold italic">Embedded in QR</span>
                </div>
              </div>

              {/* Printing Quick Guide */}
              <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl text-[10px] leading-relaxed text-indigo-900 font-semibold space-y-1">
                <p className="font-black uppercase text-[9px] text-indigo-700 tracking-wide flex items-center gap-1">
                  <Printer className="w-3.5 h-3.5" /> High-Quality Print Instructions:
                </p>
                <ol className="list-decimal list-inside pl-0.5 space-y-0.5 text-indigo-805">
                  <li>Click the main <strong>Print/Save PDF</strong> button below.</li>
                  <li>Select <strong>Save as PDF</strong> or choose your local printer.</li>
                  <li>Enable <strong>&ldquo;Background graphics&rdquo;</strong> in print settings to include the beautiful accent colors.</li>
                  <li>Ensure margins are set to <strong>&ldquo;None&rdquo;</strong> or <strong>&ldquo;Default&rdquo;</strong> for exact letter spacing.</li>
                </ol>
              </div>

              {/* Active Actions */}
              <button
                onClick={() => window.print()}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer touch-none select-none"
              >
                <Printer className="w-4 h-4" /> Print or Save as PDF
              </button>
            </div>

            {/* RIGHT COLUMN: Interactive Flyer Preview */}
            <div className="lg:col-span-8 space-y-4 printable-flyer-outer print:w-full print:max-w-none print:p-0 print:shadow-none print:border-0">
              
              <div className="flex justify-between items-center px-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Flyer Letter Preview (8.5&rdquo; x 11&rdquo;)</span>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors border border-indigo-200"
                >
                  <Download className="w-3 h-3" /> Download PDF
                </button>
              </div>

              {/* The Sheet (Perfect standard Letter ratio, gorgeous visual style) */}
              <div ref={flyerRef} className="printable-flyer-page aspect-[8.5/11] bg-white border border-gray-200 rounded-[2rem] shadow-xl p-8 sm:p-12 flex flex-col justify-between text-slate-800 relative select-none print:shadow-none print:border-0 print:rounded-none print:p-0 print:w-full print:h-[11in] print:mx-auto">
                
                {/* Style accent top bar */}
                <div className={`absolute top-0 left-0 right-0 h-4 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryBg}`} />

                {/* Top Header Row */}
                <div className="flex justify-between items-center pb-6 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-xl ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryBg} text-white flex items-center justify-center shadow-md`}>
                      <DollarSign className="w-4.5 h-4.5" />
                    </div>
                    <div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Affiliate Partner Program</span>
                      <h1 className="text-sm font-black text-slate-900 tracking-tight leading-none mt-0.5">TipValue AI</h1>
                    </div>
                  </div>
                  
                  <span className={`text-[9px] font-black uppercase tracking-widest border px-2.5 py-1 rounded-full ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} bg-slate-50`}>
                    Official Hospitality Partner
                  </span>
                </div>

                {/* Main Flyer Message Hook */}
                <div className="space-y-3 pt-6 text-center md:text-left">
                  <h2 className="text-xl sm:text-2.5xl font-black text-slate-900 tracking-tight leading-tight max-w-2xl">
                    Stop Spending Hours Pooling Tips. <span className={styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}>Automate Payouts in Seconds.</span>
                  </h2>
                  <p className="text-[11px] sm:text-xs text-gray-500 font-medium leading-relaxed max-w-2xl">
                    Automate daily tip pooling calculations, connect directly with your existing POS system, claim maximum FICA tip tax credits, and provide complete payroll transparency to your staff.
                  </p>
                </div>

                {/* Traditional Way vs TipFlow Way (Side by Side comparison) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 sm:py-6">
                  {/* Traditional */}
                  <div className="p-4 bg-rose-50/40 rounded-2xl border border-rose-100/50 space-y-2">
                    <span className="text-[9px] font-black text-rose-650 uppercase tracking-widest block">The Manual Spreadsheet Headache</span>
                    <ul className="space-y-1 text-[10px] text-gray-500 font-semibold leading-normal">
                      <li className="flex items-start gap-1.5">
                        <span className="text-rose-500 shrink-0 select-none">✕</span>
                        <span>Hours of manual math after every single shift</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-rose-500 shrink-0 select-none">✕</span>
                        <span>Prone to human entry errors and staff payouts friction</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-rose-500 shrink-0 select-none">✕</span>
                        <span>Zero QuickBooks-aligned audit or compliance trace logs</span>
                      </li>
                    </ul>
                  </div>

                  {/* TipFlow */}
                  <div className={`p-4 rounded-2xl border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} space-y-2`}>
                    <span className={`text-[9px] font-black ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} uppercase tracking-widest block`}>The Automated TipValue Standard</span>
                    <ul className="space-y-1 text-[10px] text-gray-600 font-semibold leading-normal">
                      <li className="flex items-start gap-1.5">
                        <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                        <span>Custom split formulas calculated instantly</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                        <span>1-click sync pulling sales and hours from register</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                        <span>Pristine digital ledgers matching IRS compliance paths</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Major Platform Features (Core Showcase Grid) */}
                <div className="space-y-3.5">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-1.5">
                    Core Platform Capabilities & Integrations
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    
                    {/* Feature 1 */}
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                        <Layers className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide">Flexible Pooling & Split Rules</h4>
                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
                          Easily split tips evenly, by actual hours worked, shift blocks, or customized roles and points logic.
                        </p>
                      </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide">Direct POS Integration Sync</h4>
                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
                          Connect directly with top point-of-sale systems to securely pull credit card tips, sales totals, and clock-in intervals.
                        </p>
                      </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                        <ClipboardCheck className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide">IRS & Audit General Ledger Alignment</h4>
                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
                          Pristine financial audit trails compliant with standard accounting systems to claim maximum FICA tip credits safely.
                        </p>
                      </div>
                    </div>

                    {/* Feature 4 */}
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                        <Users className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide">Transparent Self-Service Portals</h4>
                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
                          Let staff log in securely to verify shift tip details, sign off digitally, and reduce tip calculation disputes.
                        </p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Call to Action & QR Code Section (Large highlight strip at bottom) */}
                <div className={`mt-6 p-5 sm:p-6 rounded-[1.5rem] border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} flex flex-col md:flex-row items-center justify-between gap-6`}>
                  
                  <div className="space-y-1.5 text-center md:text-left grow max-w-md">
                    <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-white border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} inline-block`}>
                      Exclusive Affiliate Invitation Offer
                    </span>
                    <h4 className="text-sm font-black text-slate-855 leading-tight">
                      {customCta}
                    </h4>
                    {customNote && (
                      <p className="text-[9px] text-gray-500 font-semibold leading-normal">
                        {customNote}
                      </p>
                    )}
                  </div>

                  {/* Ticket / Scan QR Code Box */}
                  <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-gray-150 shadow-inner shrink-0 w-full md:w-auto justify-center md:justify-start">
                    {/* QR Code Container */}
                    <div className="text-center space-y-1 shrink-0">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(getSharingLink())}`}
                        alt="Referral QR Code"
                        className="w-16 h-16 border border-gray-100 rounded p-0.5 block mx-auto"
                        referrerPolicy="no-referrer"
                      />
                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block leading-none">Scan to signup</span>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-12 bg-gray-200" />

                    {/* Promo Code Info */}
                    <div className="space-y-0.5 text-center md:text-left">
                      <span className="text-[7px] font-black uppercase tracking-wider text-gray-400 block">Or Enter Promo Code</span>
                      <div className="font-mono text-sm font-black tracking-wider text-slate-855 uppercase px-2 py-0.5 bg-slate-50 border border-gray-200 rounded text-center">
                        {referralCode}
                      </div>
                      <span className="text-[7px] font-semibold text-gray-400 block leading-tight mt-0.5 max-w-[100px]">redeems 30 days free trial benefit</span>
                    </div>
                  </div>

                </div>

                {/* Footer Subtext (Legals & Info) */}
                <div className="pt-4 border-t border-gray-150 flex flex-col md:flex-row justify-between items-center text-[8px] text-gray-400 font-bold font-mono uppercase tracking-wide gap-2">
                  <span>Start automating in under 5 minutes • tipvalue.com</span>
                  <span>Compliance-Validated & Safe Over Cloud SSL</span>
                </div>

              </div>

            </div>

          </div>
        </div>
      )}
    </div>
  );
};
